// MOAT-01 — Kernvel-staff pilot registry (never in the nav, never an AdminUser session).
//
// Lists every frozen pilot version, shows one version's full report (all cohort shops + per-shop
// breakdown), and — with moat01.write — freezes a NEW version, activates a draft, stops an active
// version or archives a stopped one. There is no edit: a change is a new version. Every call is
// audited server-side. Backend: app/api/staff/pilots.py.
import { useCallback, useEffect, useState } from "react";

import { staffErrorText, staffGet, staffPost } from "../staff";
import type { PilotDocument, PilotReport } from "../pilot";
import type { StaffSession } from "../types";
import PilotReportView from "./PilotReportView";
import StaffGate from "./StaffGate";

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const [cohort, setCohort] = useState("");
  const [salt, setSalt] = useState("");
  const [units, setUnits] = useState("40");
  const [seats, setSeats] = useState("10");
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setErr(null);
    try {
      await staffPost("/pilots", {
        cohort_shop_ids: cohort.split(/[\s,]+/).filter(Boolean),
        salt: salt.trim(),
        max_ai_units_per_shop_per_day: Number(units),
        max_vendors_per_arm: Number(seats),
      });
      onCreated();
    } catch (e) {
      setErr(staffErrorText(e, "Could not create the pilot version"));
    }
  };
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-cust">Freeze a new pilot version (draft)</div>
      <label>
        Cohort shop IDs
        <input value={cohort} onChange={(e) => setCohort(e.target.value)} style={{ display: "block", width: "100%" }} />
      </label>
      <label>
        Assignment salt
        <input value={salt} onChange={(e) => setSalt(e.target.value)} style={{ display: "block", width: "100%" }} />
      </label>
      <label>
        AI units per shop per day
        <input value={units} onChange={(e) => setUnits(e.target.value)} style={{ display: "block" }} />
      </label>
      <label>
        Max vendors per group
        <input value={seats} onChange={(e) => setSeats(e.target.value)} style={{ display: "block" }} />
      </label>
      <button onClick={() => void submit()} disabled={!cohort.trim() || salt.trim().length < 16} style={{ marginTop: 8 }}>
        Create version
      </button>
      {err && <p className="risk-sub">Error: {err}</p>}
    </div>
  );
}

function PilotsBody({ session }: { session: StaffSession }) {
  const canWrite = session.scopes.includes("moat01.write");
  const [pilots, setPilots] = useState<PilotDocument[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [report, setReport] = useState<PilotReport | null>(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    try {
      const data = await staffGet<{ pilots: PilotDocument[] }>("/pilots");
      setPilots(data.pilots);
    } catch (e) {
      setErr(staffErrorText(e, "Could not list pilots"));
    }
  }, []);

  const loadReport = useCallback(async (id: string) => {
    setSelected(id);
    setReport(null);
    try {
      setReport(await staffGet<PilotReport>(`/pilots/${id}/report`));
    } catch (e) {
      setErr(staffErrorText(e, "Could not load the report"));
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const act = async (id: string, action: "activate" | "stop" | "archive") => {
    setErr(null);
    try {
      await staffPost(`/pilots/${id}/${action}`, action === "stop" ? { note: note.trim() } : {});
      await loadList();
      await loadReport(id);
    } catch (e) {
      setErr(staffErrorText(e, `Could not ${action} the pilot`));
    }
  };

  return (
    <>
      {canWrite && <CreateForm onCreated={() => void loadList()} />}
      {err && <p className="risk-sub">Error: {err}</p>}
      {pilots === null ? (
        <p className="risk-sub">Loading…</p>
      ) : pilots.length === 0 ? (
        <p className="risk-sub">No pilot versions yet.</p>
      ) : (
        <table className="cash-table" style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Pilot</th>
              <th>Status</th>
              <th>Cohort</th>
              <th>Digest</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pilots.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.pilot_key} v{p.version}
                </td>
                <td>{p.status}</td>
                <td>{p.config.cohort_shop_ids.length} shop(s)</td>
                <td>{p.config_digest.slice(0, 12)}…</td>
                <td>
                  <button className="courier-btn" onClick={() => void loadReport(p.id)}>
                    Report
                  </button>
                  {canWrite && p.status === "draft" && (
                    <button className="courier-btn" onClick={() => void act(p.id, "activate")}>
                      Activate
                    </button>
                  )}
                  {canWrite && p.status === "stopped" && (
                    <button className="courier-btn" onClick={() => void act(p.id, "archive")}>
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {selected && report && (
        <>
          <PilotReportView report={report} />
          {canWrite && report.pilot.status === "active" && (
            <div className="card" style={{ marginTop: 12 }}>
              <label>
                Stop note
                <input value={note} onChange={(e) => setNote(e.target.value)} style={{ display: "block", width: "100%" }} />
              </label>
              <button onClick={() => void act(selected, "stop")} disabled={note.trim().length < 8} style={{ marginTop: 8 }}>
                Stop pilot
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default function StaffPilots() {
  return (
    <StaffGate title="Pilots" scope="moat01.read">
      {(session) => <PilotsBody session={session} />}
    </StaffGate>
  );
}
